import { RefObject } from 'react';

import {
  Box,
  Divider,
  IconButton,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material';
import { Row, Table as TanstackTable } from '@tanstack/react-table';

import { X } from '../icons';

interface TableFooterProps<T> {
  table: TanstackTable<T>;
  isFetching: boolean;
  isError: boolean;
  hasData: boolean;
  paginationRef: RefObject<HTMLDivElement | null>;
  pageSizeSelectId: string;
  onRowClick?: (row: Row<T>) => void;
}

const TableFooter = <T,>({
  table,
  isFetching,
  isError,
  hasData,
  paginationRef,
  pageSizeSelectId,
}: TableFooterProps<T>) => {
  const pagination = table.getState().pagination;
  const rowSelection = table.getState().rowSelection;
  const numSelectedRows = Object.keys(rowSelection).length;

  return (
    <>
      <Divider />
      <Stack
        direction='row'
        alignItems='center'
        gap={2}
        justifyContent='space-between'
      >
        <Box pl={3}>
          {numSelectedRows ? (
            <Stack direction='row' alignItems='center' gap={0.5}>
              <Typography variant='body14'>
                {numSelectedRows} {numSelectedRows > 1 ? 'rows' : 'row'}{' '}
                selected
              </Typography>
              <IconButton
                size='small'
                aria-label='clear selection'
                onClick={() => {
                  table.resetRowSelection();
                }}
              >
                <X fontSize='inherit' />
              </IconButton>
            </Stack>
          ) : null}
        </Box>

        <TablePagination
          ref={paginationRef}
          component='div'
          disabled={isFetching || isError || !hasData}
          rowsPerPageOptions={[5, 10, 25]}
          count={table.getRowCount()}
          rowsPerPage={pagination.pageSize}
          page={pagination.pageIndex}
          onPageChange={(_, newPage) => {
            table.setPageIndex(newPage);
          }}
          onRowsPerPageChange={(event) => {
            table.setPageSize(Number(event.target.value));
          }}
          slotProps={{
            select: {
              inputProps: {
                id: pageSizeSelectId,
              },
            },
          }}
        />
      </Stack>
    </>
  );
};

export default TableFooter;
